import { isBlankValue } from 'components/CreationForms/utils/fields';
import {
  ExternalProperty,
  TableType,
} from 'crono-fe-common/types/externalProperty';
import {
  getNewOptionsWhenSelecting,
  splitEPsToTagsAndNotTags,
} from 'crono-fe-common/utils';
import useGetExternalProperty from 'hooks/services/externalProperty/useGetExternalProperty';
import { useState } from 'react';

const EMPTY_PROPERTIES: ExternalProperty[] = [];
const NO_EDITS: Record<number, string> = {};

const getDefaultValue = (property: ExternalProperty) => {
  if (!property.defaultValue) return undefined;
  if (property.valueType === 'Date') {
    return new Date(
      new Date(property.defaultValue).setHours(12, 0, 0, 0),
    ).toISOString();
  }
  return property.defaultValue;
};

interface UseCreationExternalPropertiesOptions {
  /** Only the mandatory properties are shown, checked and sent. */
  onlyMandatory?: boolean;
  /** The record's current values by property id, used instead of the defaults. */
  initialValues?: Record<number, string>;
  /** The record the edits belong to: each record keeps its own. */
  scope?: string;
}

/**
 * The custom fields (tags and other fields) a creation form fills in for one
 * table type. Values are keyed by property id and start from each property's
 * default, or from `initialValues` for a record that already exists; only the
 * properties of the current table type count, so switching Contact / Lead
 * never sends the other type's values.
 */
export const useCreationExternalProperties = (
  tableType: TableType,
  {
    onlyMandatory = false,
    initialValues,
    scope,
  }: UseCreationExternalPropertiesOptions = {},
) => {
  const { data } = useGetExternalProperty({
    tableType,
    onlyTag: null,
    onlyInsert: true,
  });
  const fetchedProperties = data?.data?.data ?? EMPTY_PROPERTIES;
  const properties = onlyMandatory
    ? fetchedProperties.filter((property) => property.isMandatory)
    : fetchedProperties;

  const [editsByScope, setEditsByScope] = useState<
    Record<string, Record<number, string>>
  >({});
  const scopeKey = scope ?? '';
  const editedValues = editsByScope[scopeKey] ?? NO_EDITS;
  const updateEdits = (
    update: (values: Record<number, string>) => Record<number, string>,
  ) =>
    setEditsByScope((prev) => ({
      ...prev,
      [scopeKey]: update(prev[scopeKey] ?? NO_EDITS),
    }));

  // AI fields are generated after the record is created, by the AI field
  // picker; they aren't typed in here.
  const isShown = (property: ExternalProperty) =>
    property.isEditable && !property.aiVariableSettings;
  const shownProperties = properties.filter(isShown);
  const [tagProperties, otherFieldProperties] =
    splitEPsToTagsAndNotTags(shownProperties);

  const getInitialValue = (property: ExternalProperty) => {
    const value = initialValues
      ? initialValues[property.id]
      : getDefaultValue(property);
    // A switch on screen that was never set is off.
    if (value === undefined && property.valueType === 'Boolean') {
      return isShown(property) ? 'false' : undefined;
    }
    return value;
  };

  const getValue = (property: ExternalProperty) =>
    editedValues[property.id] ?? getInitialValue(property);

  const values: Record<number, string> = {};
  properties.forEach((property) => {
    const value = getValue(property);
    if (value !== undefined) values[property.id] = value;
  });

  const setValue = (propertyId: number, value: string) =>
    updateEdits((prev) => ({ ...prev, [propertyId]: value }));

  /** Picks or unpicks a tag option; a MultiSelect tag keeps its other options. */
  const toggleTagOption = (option: string | null, property: ExternalProperty) =>
    updateEdits((prev) => {
      const current = prev[property.id] ?? getInitialValue(property);
      const next = getNewOptionsWhenSelecting({
        externalPropertyId: property.id,
        valueType: property.valueType,
        option,
        currentExternalValues:
          current === undefined
            ? []
            : [{ externalPropertyId: property.id, value: current }],
      });
      return { ...prev, [property.id]: next ?? '' };
    });

  /** Names of the mandatory properties left blank, among those the form shows. */
  const getMissingMandatory = () =>
    shownProperties
      .filter(
        (property) => property.isMandatory && isBlankValue(getValue(property)),
      )
      .map(
        (property) =>
          property.publicName ?? property.externalName ?? 'Unnamed property',
      );

  /** The filled-in values, as the create endpoints take them. */
  const toPayload = () => {
    const payload: Record<number, string> = {};
    properties.forEach((property) => {
      const value = getValue(property);
      if (!isBlankValue(value)) payload[property.id] = value as string;
    });
    return payload;
  };

  /** The values the user changed from the initial ones, to update an existing record with. */
  const getChanges = () => {
    const changes: Record<number, string> = {};
    properties.forEach((property) => {
      const value = editedValues[property.id];
      // A field emptied again matches one that had no value.
      if (value !== undefined && value !== (getInitialValue(property) ?? '')) {
        changes[property.id] = value;
      }
    });
    return changes;
  };

  return {
    tagProperties,
    otherFieldProperties,
    values,
    setValue,
    toggleTagOption,
    getMissingMandatory,
    toPayload,
    getChanges,
    isDirty: Object.keys(getChanges()).length > 0,
  };
};
